import React, { useMemo } from "react";
import { MapPin, Calendar, Terminal, Check, Sparkles } from "lucide-react";

/**
 * Shared "body" of an invitation preview — event meta, role capabilities, and
 * the About-AtelisOS blurb. Wrapping page/modal supplies the outer header
 * (event title + inviter line) and the action buttons.
 *
 * Props:
 *   preview: { event, member, invited_by } — the payload from GET /api/invite/{id}
 */
export default function InvitePreviewCard({ preview }) {
  const inviteRoleLabel = useMemo(() => {
    const m = preview?.member;
    if (!m) return "Team member";
    if (m.role === "vendor") return m.vendor_category || "Vendor";
    if (m.role === "client") return "Client";
    if (m.role === "coordinator") return "Coordinator";
    return "Team member";
  }, [preview]);

  const capabilities = ROLE_CAPABILITIES[preview?.member?.role] || ROLE_CAPABILITIES.default;
  const ev = preview?.event;
  if (!ev) return null;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <MetaRow icon={<Calendar size={14} />} label="Date" value={formatDate(ev.event_date)} />
        {ev.venue && <MetaRow icon={<MapPin size={14} />} label="Venue" value={ev.venue} />}
        {ev.event_type && (
          <MetaRow icon={<Terminal size={14} />} label="Event type" value={titleCase(ev.event_type)} />
        )}
        <MetaRow
          icon={<Sparkles size={14} />}
          label="Your role"
          value={inviteRoleLabel}
          testid="invite-role-value"
        />
      </div>

      <div className="mb-6 border-t border-hair pt-5">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3">
          WHAT YOU&apos;LL BE ABLE TO DO
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {capabilities.map((cap) => (
            <li key={cap} className="flex items-start gap-2 text-sm text-app">
              <Check size={14} strokeWidth={2.5} className="text-emerald-500 mt-0.5 shrink-0" />
              <span>{cap}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="border border-hair bg-inset rounded-sm p-4 text-sm text-app leading-relaxed">
        <span className="font-medium">About AtelisOS.</span>{" "}
        The operating system for events — a shared control center where planners, vendors, and clients
        work off the same timeline, documents, and day-of run of show. Your invitation is scoped to this
        event only.
      </div>
    </div>
  );
}

export function inviteRoleLabelFor(member) {
  if (!member) return "Team member";
  if (member.role === "vendor") return member.vendor_category || "Vendor";
  if (member.role === "client") return "Client";
  if (member.role === "coordinator") return "Coordinator";
  return "Team member";
}

const ROLE_CAPABILITIES = {
  vendor: [
    "See your deliverables, deadlines, and load-in schedule",
    "Chat with the planner and coordinate updates in real time",
    "Access documents and contracts shared with you",
    "Get day-of updates and timeline changes automatically",
  ],
  client: [
    "Track budget, guest list, and RSVP responses",
    "Approve vendors, timeline blocks, and deliverables",
    "Review the run of show and floor plan",
    "Chat with your planner + team in one thread",
  ],
  coordinator: [
    "Full read/write access to the event workspace",
    "Manage vendors, tasks, timeline, and budget",
    "Send announcements and coordinate day-of operations",
    "Access the live Command Mode for event day",
  ],
  default: [
    "Access the event workspace",
    "Chat with the planner and team",
    "See documents and updates shared with you",
  ],
};

function MetaRow({ icon, label, value, testid }) {
  return (
    <div
      className="flex items-start gap-3 border border-hair bg-inset rounded-sm p-3"
      data-testid={testid}
    >
      <span className="text-muted mt-0.5">{icon}</span>
      <div>
        <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted">{label}</div>
        <div className="text-sm text-app font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

function titleCase(s) {
  return String(s || "").replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
